import { createAsyncThunk, createEntityAdapter, createSlice, type SerializedError } from '@reduxjs/toolkit';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import type RootState from 'app-components/utils/redux-store/rootState';

import { DOMAIN_LOGOS_REDUCER_KEY, type DomainLogoProps, type DomainLogosItemsState } from './domainLogosTypes';

const domainLogosAdapter = createEntityAdapter<DomainLogoProps, string>({
  selectId: (logo: DomainLogoProps) => logo.domain,
});

const initialState: DomainLogosItemsState = domainLogosAdapter.getInitialState({
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
  resolvedMisses: {},
});

export const fetchLogosByUrl = createAsyncThunk('DOMAIN_LOGOS_URL', async (domains: string[], { rejectWithValue }) => {
  try {
    const url = `${getServiceUrl()}/logos/domains`;
    const response = await httpClientInterface.send('POST', url, { domains });
    const data = response.data as { resources: DomainLogoProps[] };
    return data.resources;
  } catch (error) {
    return rejectWithValue(error || 'Unexpected error');
  }
});

export const fetchLogoById = createAsyncThunk('DOMAIN_LOGOS_ID', async (id: string, { rejectWithValue }) => {
  try {
    const url = `${getServiceUrl()}/logos/domains/${id}`;
    const response = await httpClientInterface.send('GET', url);
    const newLogo = response.data as DomainLogoProps;
    if (newLogo.id && newLogo.domain) {
      return newLogo;
    } else {
      return rejectWithValue('Malformed logo! - no id or domain');
    }
  } catch (error) {
    return rejectWithValue(error || 'Unexpected error');
  }
});

const selectDomainLogosState = (state: RootState) => state[DOMAIN_LOGOS_REDUCER_KEY];

export const domainLogosSelectors = domainLogosAdapter.getSelectors(selectDomainLogosState);

const domainLogosSlice = createSlice({
  name: DOMAIN_LOGOS_REDUCER_KEY,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLogosByUrl.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLogosByUrl.fulfilled, (state, action) => {
        domainLogosAdapter.upsertMany(state, action.payload);
        // Record requested domains the service resolved without a logo, so
        // remounts don't refetch them; rejected lookups stay retryable.
        const returnedDomains = new Set(action.payload.map((logo) => logo.domain));
        for (const domain of action.meta.arg) {
          if (returnedDomains.has(domain)) {
            delete state.resolvedMisses[domain];
          } else {
            state.resolvedMisses[domain] = true;
          }
        }
        state.isLoading = false;
      })
      .addCase(fetchLogosByUrl.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message as SerializedError;
      })
      .addCase(fetchLogoById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLogoById.fulfilled, (state, action) => {
        domainLogosAdapter.upsertOne(state, action.payload);
        delete state.resolvedMisses[action.payload.domain];
        state.isLoading = false;
      })
      .addCase(fetchLogoById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message as SerializedError;
      });
  },
});

export { domainLogosSlice };
